import React, { useRef, useState, useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import clsx from "clsx";
import CustomMapMarker from "../common/custom-map-marker";
import { Store } from "../../__generated__/graphql";
import {
  getCurrentAppVersion,
  isAndroidApp,
  isIOSApp,
  isWeb,
} from "../../utils/user-agent";
import { DEFAULT_MAP_LAT, DEFAULT_MAP_LNG } from "../../utils/map-defaults";
import instagramIcon from "../../Routes/store/assets/instagram.jpeg";
import naverIcon from "../../Routes/store/assets/naver.png";
import { compareVersions } from "compare-versions";

interface StoreDetailsProps {
  loading: boolean;
  store: Store | undefined;
  onTabClick?: (section: "reserve" | "timeSale" | "review") => void;
}

const StoreDetails: React.FC<StoreDetailsProps> = ({
  loading,
  store,
  onTabClick,
}) => {
  function copyToClipboard(text: string) {
    // 클립보드에 텍스트를 쓰기
    navigator.clipboard.writeText(text).then(() => {
      // 복사 성공
      alert("텍스트가 클립보드에 복사되었습니다!");
    });
  }
  const mapElement = useRef<HTMLDivElement | null>(null);
  const [isMapToggleOpen, setIsMapToggleOpen] = useState(false);
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);
  const [AddressY] = useState<number>(Number(store?.lat) || DEFAULT_MAP_LAT);
  const [AddressX] = useState<number>(Number(store?.lng) || DEFAULT_MAP_LNG);
  const { naver } = window;
  let map: naver.maps.Map;

  useEffect(() => {
    if (!mapElement.current || !naver) {
      return;
    }
    const center = new naver.maps.LatLng(AddressY, AddressX);
    const mapOptions: naver.maps.MapOptions = {
      center,
      zoom: 16,
      minZoom: 7,
      maxZoom: 19,
      zoomControl: false,
      mapDataControl: false,
      scaleControl: false,
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    map = new naver.maps.Map(mapElement.current, mapOptions);
    if (store) {
      addMarker(store);
    }
  }, [store, AddressX, AddressY]);

  const addMarker = (store: Store) => {
    const { title, lat, lng } = store;
    try {
      let newMarker = new naver.maps.Marker({
        position: new naver.maps.LatLng(lat as number, lng as number),
        map,
        title,
        clickable: true,
        icon: {
          content: CustomMapMarker(),
          size: new naver.maps.Size(20, 20),
        },
      });
      newMarker.setTitle(title);
    } catch (e) {}
  };

  return (
    <div className="w-full bg-white rounded-b-2xl overflow-hidden">
      <div className="w-full flex overflow-hidden bg-white">
        <div className="p-4 w-full">
          {loading ? (
            <>
              <Skeleton style={{ height: 17 }} />
              <Skeleton style={{ height: 17 }} />
              <Skeleton style={{ height: 17 }} />
            </>
          ) : (
            <>
              <div className="space-y-3 mb-4">
                {store?.pack?.category && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-base font-semibold">
                      카테고리
                    </span>
                    <span className="text-black text-base">
                      {store?.pack?.category}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-gray-600 text-base font-semibold">
                    가게위치
                  </span>
                  <span className="text-black text-base">{store?.address}</span>
                </div>

                {store?.detailAddress && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-base font-semibold">
                      상세 주소
                    </span>
                    <div
                      className="flex items-center cursor-pointer"
                      onClick={() => setIsNavigationOpen((prev) => !prev)}
                    >
                      <span className="text-black text-base">
                        {store.detailAddress}
                      </span>
                      {store?.navigationInfo && (
                        <svg
                          className={`w-4 h-4 ml-2 transform transition-transform ${
                            isNavigationOpen ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                )}

                {store?.navigationInfo && isNavigationOpen && (
                  <div className="bg-gray-50 p-3 rounded-lg mt-2">
                    <span className="text-gray-600 text-sm whitespace-pre-wrap">
                      {store.navigationInfo}
                    </span>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-100 pt-4 mb-4">
                <div className="text-black text-base font-semibold mb-3">
                  추가 정보
                </div>
                <div className="flex flex-wrap gap-2">
                  {isAndroidApp() && (
                    <div
                      className="px-3 py-2 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors"
                      onClick={() => copyToClipboard(store?.address as string)}
                    >
                      📋 주소 복사하기
                    </div>
                  )}

                  <div
                    className="px-3 py-2 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={() => setIsMapToggleOpen((prev) => !prev)}
                  >
                    {isMapToggleOpen ? "🗺️ 지도닫기" : "🗺️ 지도보기"}
                  </div>

                  {
                    // 아이폰 또는 웹에서는 항상 인스타그램과 네이버 길찾기를 노출
                    (isIOSApp() ||
                      isWeb() ||
                      // 안드로이드인 경우, 현재 버전이 4.3.0 이상일 때만 노출
                      (() => {
                        const currentVersion = getCurrentAppVersion();
                        return (
                          isAndroidApp() &&
                          currentVersion !== null &&
                          compareVersions(currentVersion as string, "4.3.0") >=
                            0
                        );
                      })()) && (
                      <>
                        {store?.naverPlaceUrl && (
                          <a
                            className="flex items-center px-3 py-2 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                            href={store?.naverPlaceUrl}
                          >
                            <img
                              src={naverIcon}
                              alt="네이버 길찾기"
                              className="w-4 h-4 rounded mr-2"
                            />
                            <span>네이버 길찾기</span>
                          </a>
                        )}
                        {store?.instagramUrl && (
                          <a
                            className="flex items-center px-3 py-2 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                            href={store?.instagramUrl}
                          >
                            <img
                              src={instagramIcon}
                              alt="인스타그램"
                              className="w-4 h-4 rounded mr-2"
                            />
                            <span>인스타</span>
                          </a>
                        )}
                      </>
                    )
                  }
                </div>
              </div>

              <div
                className={clsx("border-t border-gray-100 pt-4", {
                  hidden: !isMapToggleOpen,
                })}
              >
                <div className="text-black text-base font-semibold mb-3">
                  🗺️ 위치 지도
                </div>
                <div
                  ref={mapElement}
                  id="map"
                  style={{
                    width: "100%",
                    height: "180px",

                    borderRadius: 12,
                    border: "1px solid #e0e0e0",
                  }}
                  className={clsx({ hidden: !isMapToggleOpen })}
                ></div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 네비게이션 탭 - 하단 밀착 */}
      <div className="flex w-full border-t border-gray-100 bg-white">
        <button
          onClick={() => onTabClick?.("reserve")}
          className="flex-1 py-4 text-base font-bold text-gray-800 hover:bg-gray-50 transition-colors"
        >
          예약
        </button>
        <button
          onClick={() => onTabClick?.("timeSale")}
          className="flex-1 py-4 text-base font-bold text-gray-800 border-x border-gray-100 hover:bg-gray-50 transition-colors"
        >
          바로픽업
        </button>
        <button
          onClick={() => onTabClick?.("review")}
          className="flex-1 py-4 text-base font-bold text-gray-800 hover:bg-gray-50 transition-colors"
        >
          리뷰
        </button>
      </div>
    </div>
  );
};

export default StoreDetails;
